import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, Divider, Stack, Table, TableBody, TableCell, TableRow, Typography } from '@mui/material';
import { Refresh } from '@mui/icons-material';
import { ConfirmationDialog } from '@components/common/ConfirmationDialog.tsx';
import {
  AnalyticsStatus,
  AnalyticsTestResult,
  backfillServerProperties,
  getAnalyticsStatus,
  sendAnalyticsTestEvent,
} from '../../services/analytics-developer.service';
import { toastService } from '../../services/toast.service';

const formatTime = (iso: string | null) => (iso ? new Date(iso).toLocaleString() : '—');

// Серверна аналітика (Amplitude): стан відправки, тестова подія від свого імені, разове заповнення серверних властивостей.
// Події з мобільного застосунку тут не видно — їх шле застосунок напряму в Amplitude.
export const DevAmplitudeTools: React.FC = () => {
  const [status, setStatus] = useState<AnalyticsStatus | null>(null);
  const [isStatusLoading, setIsStatusLoading] = useState(false);
  const [testResult, setTestResult] = useState<AnalyticsTestResult | null>(null);
  const [isTestRunning, setIsTestRunning] = useState(false);
  const [isBackfillConfirmOpen, setIsBackfillConfirmOpen] = useState(false);
  const [isBackfillRunning, setIsBackfillRunning] = useState(false);

  const loadStatus = useCallback(() => {
    setIsStatusLoading(true);
    getAnalyticsStatus()
      .then(setStatus)
      .catch(() => toastService.error('Не вдалося отримати стан аналітики'))
      .finally(() => setIsStatusLoading(false));
  }, []);

  useEffect(() => loadStatus(), [loadStatus]);

  const handleTestEvent = () => {
    setIsTestRunning(true);
    sendAnalyticsTestEvent()
      .then(result => {
        setTestResult(result);
        loadStatus();
      })
      .catch(() => toastService.error('Не вдалося надіслати тестову подію'))
      .finally(() => setIsTestRunning(false));
  };

  const handleConfirmBackfill = () => {
    setIsBackfillConfirmOpen(false);
    setIsBackfillRunning(true);
    backfillServerProperties()
      .then(result => {
        if (result.enabled) toastService.success(`Серверні властивості надіслано в Amplitude: ${result.users} клієнтів`);
        else toastService.error('Amplitude вимкнено: на сервері не задано AMPLITUDE_API_KEY');
        loadStatus();
      })
      .catch(() => toastService.error('Не вдалося заповнити серверні властивості'))
      .finally(() => setIsBackfillRunning(false));
  };

  return (
    <Box sx={{ maxWidth: 720 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Typography variant="h6">Стан серверної аналітики</Typography>
        <Button startIcon={<Refresh />} disabled={isStatusLoading} onClick={loadStatus}>
          Оновити
        </Button>
      </Stack>
      {status && !status.enabled && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          На сервері не задано AMPLITUDE_API_KEY — бекенд нічого не шле, факти лише позначаються в журналі.
        </Alert>
      )}
      {status && (
        <Table size="small" sx={{ mb: 1 }}>
          <TableBody>
            <TableRow>
              <TableCell>Ключ Amplitude</TableCell>
              <TableCell>{status.enabled ? 'задано' : 'не задано'}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Запитів з моменту старту сервера</TableCell>
              <TableCell>
                успішних: {status.transport.sentRequests}, невдалих: {status.transport.failedRequests}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Остання успішна відправка</TableCell>
              <TableCell>{formatTime(status.transport.lastSuccessAt)}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Остання помилка</TableCell>
              <TableCell>
                {status.transport.lastFailure
                  ? `${formatTime(status.transport.lastFailure.at)} (${status.transport.lastFailure.context}): ${status.transport.lastFailure.message}`
                  : '—'}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Факти внесення даних за добу</TableCell>
              <TableCell>
                надіслано: {status.journal.last24h.sent}, позначено без відправки: {status.journal.last24h.marked}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Факти внесення даних усього</TableCell>
              <TableCell>
                надіслано: {status.journal.total.sent}, позначено без відправки: {status.journal.total.marked}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      )}
      <Typography variant="body2" color="text.secondary">
        Лічильники запитів — з моменту останнього старту сервера. «Позначено без відправки» — факти, коли Amplitude
        вимкнено або дію виконав не сам клієнт (напр. експерт), і записи, що були до запуску аналітики.
      </Typography>

      <Divider sx={{ my: 3 }} />

      <Typography variant="h6">Тестова подія</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Надсилає від вашого імені подію analytics_test_event і серверні властивості (signup_date, main_goal_type,
        goals_count). Знайдіть себе в Amplitude (User Look-up) за user_id нижче — він має збігатися з тим, що шле
        мобільний застосунок.
      </Typography>
      <Button variant="contained" disabled={isTestRunning} onClick={handleTestEvent}>
        {isTestRunning ? 'Надсилаємо…' : 'Надіслати'}
      </Button>
      {testResult && (
        <Alert severity={testResult.enabled && testResult.eventSent && testResult.propertiesSent ? 'success' : 'error'} sx={{ mt: 2 }}>
          {!testResult.enabled
            ? 'Amplitude вимкнено: на сервері не задано AMPLITUDE_API_KEY.'
            : `user_id: ${testResult.userId}. Подія: ${testResult.eventSent ? 'прийнято' : 'помилка'}, властивості: ${testResult.propertiesSent ? 'прийнято' : 'помилка'}.`}
        </Alert>
      )}

      <Divider sx={{ my: 3 }} />

      <Typography variant="h6">Заповнити серверні властивості</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Надсилає в Amplitude усім наявним клієнтам властивості, які ставить бекенд: signup_date, main_goal_type,
        goals_count. Нові клієнти й зміни цілей отримують їх самі; це — для тих, хто був до запуску аналітики. Повторний
        запуск безпечний. Властивості з'являються лише в нових подіях, минулі їх не отримують.
      </Typography>
      <Button variant="contained" disabled={isBackfillRunning} onClick={() => setIsBackfillConfirmOpen(true)}>
        {isBackfillRunning ? 'Надсилаємо…' : 'Заповнити'}
      </Button>

      <ConfirmationDialog
        open={isBackfillConfirmOpen}
        onClose={() => setIsBackfillConfirmOpen(false)}
        onConfirm={handleConfirmBackfill}
        title="Заповнити серверні властивості"
        message="Надіслати signup_date, main_goal_type і goals_count в Amplitude для всіх наявних клієнтів?"
        confirmText="Заповнити"
        cancelText="Скасувати"
      />
    </Box>
  );
};
